import {currentUser, storage} from './FirestoreService';
import {ref, getDownloadURL, uploadString, uploadBytes, listAll, StorageReference, ListResult} from 'firebase/storage';

/**
 * Retrieve the download URL for a file from Google Cloud Storage.
 */
const getURL = (path: string | StorageReference): Promise<string> => {
  if (typeof path === 'string') {
    return getDownloadURL(ref(storage, path));
  } else {
    return getDownloadURL(path);
  }
};

/**
 * Retrieve a file from Google Cloud Storage using a path.
 */
const getFile = async (path: string): Promise<Blob | undefined> => {
  try {
    const url = await getURL(path);
    return getFileByURL(url);
  } catch (error) {
    console.error('failed to get file', error);
    return undefined;
  }
};

/**
 * Retrieve a file from Google Cloud Storage using a URL.
 * This technically would work with any URL, but it's in here for organizational reasons.
 */
const getFileByURL = async (url: string, headers?: Record<string, string>): Promise<Blob | undefined> => {
  const fetchOptions: {method: string; headers?: Record<string, string>} = {method: 'GET'};
  if (headers) {
    fetchOptions.headers = headers;
  }
  try {
    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw Error(response.statusText);
    return await response.blob();
  } catch (error) {
    console.error('failed to fetch file', error);
    return undefined;
  }
};

/**
 * Retrieve an image from Google Cloud Storage using a path.
 */
const getImage = async (bucket: string, path: string): Promise<string | undefined> => {
  const blob = await getFile(path);
  return handleImageBlob(blob);
};

/**
 * Retrieve an image from Google Cloud Storage using a URL.
 */
const getImageByURL = async (url: string): Promise<string | undefined> => {
  const blob = await getFileByURL(url);
  return handleImageBlob(blob);
};

/**
 * Save an image to the given path in Google Cloud Storage.
 */
const saveImage = (image: string, path: string) =>
  uploadString(ref(storage, path), image, 'base64', {cacheControl: 'private, max-age=2592000'});

/**
 * Upload a file to Google Cloud Storage.
 */
const uploadFile = async (file: File, path: string): Promise<string> => {
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  return path;
};

/**
 * Generates a signed URL for the document. Can be used for viewing the document in the browser.
 */
const getSignedUrl = async (docId: string, page: number | null = null): Promise<string> => {
  const idToken = await currentUser()?.getIdToken();
  const urls: Record<string, string> = {
    staging: 'https://us-central1-beyondmaps-dev.cloudfunctions.net/generateSignedUrl-staging',
    production: 'https://us-central1-northpoint-production.cloudfunctions.net/generateSignedUrl',
  };
  const cloudFunctionUrl = urls[process.env.VUE_APP_GCP_ENV || 'staging'];

  const response = await fetch(cloudFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      docId: docId,
      page: page,
    }),
  });
  const json = await response.json();
  if (!json.signedUrl) {
    throw new Error('Failed to retrieve signed URL: Response does not contain signedUrl.');
  }
  return json.signedUrl;
};

/**
 * Helper function to get the base64 image representation from a blob pulled out of Google Cloud Storage.
 */
const handleImageBlob = async (blob: Blob | undefined): Promise<string | undefined> => {
  if (!blob) return;

  return new Promise((resolve) => {
    const fr = new FileReader();
    // The result consists of an encoding string, followed by a comma, followed by the data.
    fr.onloadend = (e) => {
      const result = (e.target as FileReader).result as string;
      resolve(result.split(',')[1]);
    };
    fr.readAsDataURL(new Blob([blob]));
  });
};

const getJsonByURL = (url: string, headers?: Record<string, string>): Promise<unknown> =>
  getFileByURL(url, headers)
    .then(handleJsonBlob);

/**
 * Helper function to get the JSON representation from a blob pulled out of Google Cloud Storage.
 */
const handleJsonBlob = (blob: Blob | undefined): Promise<unknown> => {
  if (!blob) return Promise.resolve(undefined);

  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onloadend = (e) => {
      const result = (e.target as FileReader).result as string;
      resolve(JSON.parse(result));
    };
    fr.readAsText(blob);
  });
};

const listAllObjects = (path: string): Promise<ListResult> => {
  const listRef = ref(storage, path);
  return listAll(listRef);
};

const getRef = (path: string): StorageReference => {
  return ref(storage, path);
};

export {
  getURL,
  getFile,
  getFileByURL,
  getImage,
  getImageByURL,
  saveImage,
  uploadFile,
  listAllObjects,
  getRef,
  getSignedUrl,
  getJsonByURL,
};
